/**
 * JobCardSearchAutoComplete
 * ---------------------------------------------------------------------------
 * Drop-in replacement for the bare <Input> in the Job Cards kanban search.
 * Adds a live drop-down of customer / vehicle / job-card / booking / invoice
 * hits powered by /api/global-search (debounced 200ms, fires from 2 chars).
 *
 * The SM workflow this targets:
 *   1. Customer rings → SM starts typing `041` while still on the kanban.
 *   2. As they type, a dropdown surfaces matching customers (with rego +
 *      last-visit date), vehicles, and existing JCs.
 *   3. SM clicks one → for Customers/Vehicles the input value is replaced
 *      with the precise identifier (mobile / rego) so the kanban filters
 *      to ALL their work; for JC hits the detail modal opens via the
 *      existing `?id=` deep-link convention.
 *
 * Keyboard:
 *   • ArrowDown / ArrowUp moves the cursor.
 *   • Enter selects highlighted; Esc closes; outside-click closes.
 *
 * Props:
 *   value            current search string (controlled)
 *   onChange(value)  called when the SM types
 *   onPickIdentifier(value)  called when SM picks a customer/vehicle —
 *                            replace search box with the precise value.
 *   onOpenJobCard(id)        called when SM picks a JC hit.
 *   placeholder, ariaLabel
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { withSessionHash } from '@/lib/authFetch';
import {
  Search, User as UserIcon, Car, ClipboardList, Calendar, Receipt, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Compact type → icon / colour map mirroring the Related-Items panel so
// the visual vocabulary across the kanban is consistent.
const TYPE_META = {
  customer: { Icon: UserIcon, color: 'text-slate-700 bg-slate-100', label: 'Customer' },
  vehicle:  { Icon: Car,      color: 'text-emerald-700 bg-emerald-50', label: 'Vehicle' },
  job_card: { Icon: ClipboardList, color: 'text-blue-700 bg-blue-50', label: 'Job Card' },
  booking:  { Icon: Calendar, color: 'text-amber-700 bg-amber-50', label: 'Booking' },
  invoice:  { Icon: Receipt,  color: 'text-violet-700 bg-violet-50', label: 'Invoice' },
};

export default function JobCardSearchAutoComplete({
  value,
  onChange,
  onPickIdentifier,
  onOpenJobCard,
  placeholder = 'Search rego / name / mobile…',
  ariaLabel = 'Search job cards',
}) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [cursor, setCursor] = useState(-1);
  // Flag flipped on for one render-pass after the SM picks a result.
  // Prevents the debounced search effect from re-opening the dropdown when
  // it re-fires due to the value-prop changing to the picked identifier
  // (otherwise the panel pops back open on top of the now-filtered kanban).
  const justPickedRef = useRef(false);

  // Flatten the {customers,vehicles,job_cards,bookings,invoices} response
  // into a single ordered list — easier to keyboard-navigate.
  const flat = useMemo(() => {
    if (!results) return [];
    const ORDER = ['customers', 'vehicles', 'job_cards', 'bookings', 'invoices'];
    const out = [];
    ORDER.forEach((k) => {
      (results[k] || []).slice(0, 5).forEach((r) => out.push(r));
    });
    return out;
  }, [results]);

  // Debounced fetch — fires at 2 chars min, cancels stale in-flight reqs.
  useEffect(() => {
    const term = (value || '').trim();
    if (term.length < 2) {
      setResults(null);
      setOpen(false);
      return undefined;
    }
    // If the value change is the result of the SM JUST picking a row
    // (we set the box value to the mobile/rego), don't re-open the
    // dropdown — the kanban below is already filtered. Clear the flag
    // so the next user-typed change re-opens normally.
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return undefined;
    }
    let alive = true;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/global-search?q=${encodeURIComponent(term)}&limit_each=5`,
          { credentials: 'include' },
        );
        if (r.ok && alive) {
          const body = await r.json();
          setResults(body);
          setOpen(true);
          setCursor(-1);
        }
      } catch (_e) { /* swallow */ }
      finally { if (alive) setBusy(false); }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [value]);

  // Outside-click + Esc to close.
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Pull the precise identifier we'd use as the kanban filter (mobile for
  // customers, rego for vehicles). For JCs/bookings/invoices we route
  // directly to the relevant resource instead.
  const handlePick = (r) => {
    setOpen(false);
    setResults(null);  // also clear so the dropdown can't re-appear
    if (!r) return;
    // Flag the upcoming value-prop change so the debounced search effect
    // skips it (otherwise the dropdown pops back open over the kanban).
    justPickedRef.current = true;
    if (r.type === 'job_card' && onOpenJobCard) {
      onOpenJobCard(r.id);
      return;
    }
    if (r.type === 'customer') {
      // Subtitle usually starts with mobile — extract digits-only suffix.
      const mob = (r.subtitle || '').match(/0?\d{8,10}/);
      if (mob && onPickIdentifier) { onPickIdentifier(mob[0]); return; }
      if (onPickIdentifier) onPickIdentifier(r.title.split(' ')[0]);
      return;
    }
    if (r.type === 'vehicle') {
      // Title usually opens with the rego.
      const rego = (r.title || '').split(/[\s·]/)[0];
      if (onPickIdentifier && rego) { onPickIdentifier(rego); return; }
    }
    // bookings / invoices — fall back to navigating via href.
    if (r.href) window.open(withSessionHash(r.href), '_blank', 'noopener');
  };

  const handleKey = (e) => {
    if (!open || flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (c + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      if (cursor >= 0) {
        e.preventDefault();
        handlePick(flat[cursor]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="jc-search-autocomplete-wrap">
      <Input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (flat.length > 0) setOpen(true); }}
        onKeyDown={handleKey}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="w-60 pl-8 bg-white"
        data-testid="jc-search-input"
        autoComplete="off"
      />
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
      {busy && (
        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-indigo-500 animate-spin pointer-events-none" />
      )}

      {open && (flat.length > 0 ? (
        <div
          className="absolute left-0 top-full mt-1 w-[24rem] max-h-80 overflow-y-auto bg-white border border-indigo-200 rounded-lg shadow-xl ring-1 ring-indigo-100 z-50"
          data-testid="jc-search-autocomplete-dropdown"
        >
          {flat.map((r, i) => {
            const meta = TYPE_META[r.type] || { Icon: Search, color: 'text-slate-700 bg-slate-100', label: r.type };
            const Icon = meta.Icon;
            const active = i === cursor;
            return (
              <button
                key={`${r.type}-${r.id}`}
                type="button"
                tabIndex={-1}
                onMouseEnter={() => setCursor(i)}
                onClick={() => handlePick(r)}
                className={`
                  w-full text-left px-3 py-2 flex items-start gap-2 border-b border-slate-100 last:border-b-0
                  ${active ? 'bg-indigo-50' : 'hover:bg-slate-50'}
                  transition-colors
                `}
                data-testid={`jc-search-result-${r.type}-${r.id}`}
              >
                <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full shrink-0 ${meta.color}`}>
                  <Icon className="h-3 w-3" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate">{r.title}</p>
                  {r.subtitle && (
                    <p className="text-[11px] text-slate-500 truncate">{r.subtitle}</p>
                  )}
                </div>
                <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 font-bold shrink-0 mt-1 pl-2 border-l border-slate-200">
                  {meta.label}
                </span>
              </button>
            );
          })}
          <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100 bg-slate-50">
            <strong>↵</strong> select &nbsp;·&nbsp; <strong>↑↓</strong> navigate &nbsp;·&nbsp; <strong>Esc</strong> close
          </div>
        </div>
      ) : results !== null && !busy ? (
        <div
          className="absolute left-0 top-full mt-1 w-[24rem] bg-white border border-slate-200 rounded-lg shadow-md ring-1 ring-slate-100 z-50 px-4 py-3 text-center"
          data-testid="jc-search-autocomplete-no-matches"
        >
          <p className="text-xs text-slate-500">
            No customers, vehicles or job cards match "<strong>{(value || '').trim()}</strong>".
          </p>
          <p className="text-[10px] text-slate-400 mt-1">Try fewer characters or a different number.</p>
        </div>
      ) : null)}
    </div>
  );
}
